import {
  useApiUrl,
  useCustomMutation,
  useGetLocale,
  useTranslate,
} from "@refinedev/core";
import React, { useState } from "react";
import {
  Flex,
  Typography,
  Col,
  Row,
  Button,
  Space,
  message,
  MenuProps,
  Modal,
  Dropdown,
  Popconfirm,
} from "antd";
import { DateField } from "@refinedev/antd";
import { useGo, useList, useInvalidate } from "@refinedev/core";
import {
  ArrowRightIcon,
  BookOpenTextIcon,
  BracketsCurlyIcon,
  ClockCountdownIcon,
  DotsThreeVerticalIcon,
  QueueIcon,
  SparkleIcon,
  VideoIcon,
} from "@phosphor-icons/react";
import { EyeOutlined, CopyOutlined, PlusOutlined } from "@ant-design/icons";
import { CanAccess } from "@refinedev/core";

import { addDateTz } from "pages/media/utils";
import { useAntTheme } from "hooks/useAntTheme";
import {
  MediaProjectResponse,
  MediaProjectPublicationBase,
  ProjectMediaResponse,
  ContactResponse,
} from "../../types";
import { MediaStatus } from "pages/media/components/MediaStatusComponent";
import { HoverableCard } from "components/HoverableCard";
import PreviewVideoButton from "./PreviewVideoButton";
import { ProjectMediaUpload } from "./ProjectMediaUpload";
import backgroundImg from "../../../../assets/background_3dmesh_white.jpg";
import { MEDIAPLAYER_URL } from "../../../../constants";
import DownloadButton from "./DownloadButton";
import { ProjectProgress } from "./ProjectProgress";
import { ContactsPopover } from "pages/media/components/ContactsPopover";
import { useOpenPublication } from "../hooks/useOpenPublication";
import { useProjectPublications } from "../hooks/useProjectPublications";
import { getLanguageName } from "helpers/core";
import { hasHeyGeniusDocTag } from "../helpers/helper";

interface ProjectMediasProps {
  medias: ProjectMediaResponse[];
  project: MediaProjectResponse;
  onEditCuePoints: (mediaId: string) => void;
  onEditTemplates: (mediaId: string) => void;
  refetchProjectDetail: () => void;
}

export const ProjectMedias: React.FC<ProjectMediasProps> = ({
  medias,
  project,
  onEditCuePoints,
  onEditTemplates,
  refetchProjectDetail,
}) => {
  const { publications } = useProjectPublications(project.id);
  const go = useGo();
  const { data: contactsData } = useList<ContactResponse>({
    resource: `media/${project.organization_id}/contacts`,
  });

  const getLocale = useGetLocale();
  const currentLanguage = getLocale();

  return (
    <Flex vertical>
      <Row gutter={20}>
        {medias.map((media, index) => {
          const mediaPublications = publications?.filter(
            // use contact_id+language as pivot instead of mediaId that can change during
            // the publication workflow in the backend
            (x) =>
              media?.id &&
              x.contact_id === media.contact_id &&
              x.language === media.language
          );

          const contact = contactsData?.data.find(
            (x) => x.id === media.contact_id
          );

          return (
            <Col
              key={index}
              xl={{ flex: "33%" }}
              md={{ flex: "50%" }}
              xs={{ flex: "100%" }}
            >
              <ProjectMediaCard
                media={media}
                onEditCuePoints={onEditCuePoints}
                onEditTemplates={onEditTemplates}
                project={project}
                mediaPublications={mediaPublications}
                refetchProjectDetail={refetchProjectDetail}
                index={index}
              />
              <Flex justify="space-between" style={{ marginTop: 10 }}>
                <Flex vertical>
                  {contact && (
                    <ContactsPopover contacts={[contact]}>
                      <Typography.Text
                        strong
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          if (contact?.id)
                            go({
                              to: {
                                resource: "media_contacts",
                                id: contact?.id,
                                action: "edit",
                              },
                            });
                        }}
                      >
                        {contact?.firstname} {contact?.lastname}
                      </Typography.Text>
                    </ContactsPopover>
                  )}
                  <Flex align="center" wrap="wrap">
                    {media.created && (
                      <DateField
                        type="secondary"
                        value={addDateTz(media.created)}
                        format="L"
                      />
                    )}

                    <Typography.Text
                      type="secondary"
                      style={{
                        margin: "0 8px",
                        fontSize: "20px",
                      }}
                    >
                      &middot;
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {getLanguageName(media.language, currentLanguage ?? "-")}
                    </Typography.Text>
                  </Flex>
                </Flex>

                <Flex vertical gap={10}>
                  {media.status && <MediaStatus status={media.status} />}
                </Flex>
              </Flex>
            </Col>
          );
        })}
      </Row>
    </Flex>
  );
};

const ProjectMediaCard = ({
  media,
  onEditCuePoints,
  onEditTemplates,
  project,
  mediaPublications,
  refetchProjectDetail,
  index,
}: {
  media: ProjectMediaResponse;
  project: MediaProjectResponse;
  onEditCuePoints: (mediaId: string) => void;
  onEditTemplates: (mediaId: string) => void;
  mediaPublications: MediaProjectPublicationBase[] | undefined;
  refetchProjectDetail: () => void;
  index: number;
}) => {
  const t = useTranslate();
  const { Text } = Typography;
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation();
  const { theme } = useAntTheme();
  const apiUrl = useApiUrl();
  const isDocHeyGenius = hasHeyGeniusDocTag(project?.tags);
  // Publish state lives on the publication, not the media (MED-6885) — a
  // publication record for this media's instance means it's live.
  const isLive = Boolean(mediaPublications?.length);

  const { openPublication } = useOpenPublication({
    projectId: project.id,
    publicationId: mediaPublications?.[0]?.instance_id,
    requiresAccessToken: !isLive,
    isDocHeyGenius,
    mediaStatus: media.status,
    isLive,
  });

  const storyboard = project?.storyboards.find(
    (storyboard) => storyboard.language === media?.language
  );

  const handleCopyId = () => {
    navigator.clipboard
      .writeText(media.id ?? "")
      .then(() => {
        message.success(
          `${t("projects.components.ProjectDocumentsList.idCopied")} ${
            media.id
          }`
        );
      })
      .catch((error) => {
        message.error(
          t("projects.components.ProjectDocumentsList.idCopyFailed")
        );
        console.error("Copy to clipboard failed:", error);
      });
  };

  const handleRegenerate = async () => {
    try {
      await mutateAsync({
        url: `${apiUrl}/media/media/${media.id}/regenerate`,
        method: "post",
        values: {},
        successNotification: {
          type: "success",
          message: t("projects.components.ProjectMedias.regenerateSuccess"),
        },
      });

      // restart polling media status
      setTimeout(() => {
        refetchProjectDetail();
      }, 3000); // wait 3s for server to process (not sure necessary but didn't work without)

      // invalidate publications, as regenerating removes old publications
      invalidate({
        resource: `media/projects/${project.id}/publications`,
        invalidates: ["list"],
      });
    } catch (e) {
      console.error(e);
    }
  };

  const [openedModal, setOpenedModal] = useState(false);
  const go = useGo();
  const openModal = () => {
    setOpenedModal(true);
  };
  const closeModal = () => setOpenedModal(false);

  const menuItems: MenuProps["items"] = [
    {
      key: "copy-id",
      label: (
        <Button
          key="copy-id"
          type="text"
          size="large"
          style={{ background: "none" }}
          icon={<CopyOutlined style={{ color: theme.colorText }} />}
        >
          {t("projects.components.ProjectMedias.copyId")}
        </Button>
      ),
      onClick: handleCopyId,
    },
    {
      key: "download",
      label: <DownloadButton url={media.url || ""} size="large" />,
    },
    {
      key: "upload-video",
      label: (
        <Button
          type="text"
          style={{ background: "none" }}
          size="large"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          {t("projects.components.ProjectMedias.uploadAVideo")}
        </Button>
      ),
    },
    ...(Boolean(media?.id) &&
    !["Pending", "Draft", "Generating"].includes(media.status)
      ? [
          {
            key: "regenerate",
            label: (
              <Popconfirm
                title={t("buttons.confirm")}
                onConfirm={handleRegenerate}
              >
                <Button
                  icon={
                    <span className="anticon">
                      <SparkleIcon
                        style={{ color: theme.colorText }}
                        weight="bold"
                      />
                    </span>
                  }
                  type="text"
                  style={{ background: "none" }}
                  size="large"
                >
                  {t("media.media.detail.regenerate")}
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
    ...(media.id
      ? [
          {
            key: "debug",
            label: (
              <CanAccess resource="media_generation_steps" action="show">
                <Space>
                  <Button
                    onClick={() => {
                      go({
                        to: {
                          resource: "media_media",
                          id: media.id,
                          action: "show",
                        },
                      });
                    }}
                    icon={
                      <span className="anticon">
                        <BracketsCurlyIcon
                          style={{ color: theme.colorText }}
                          weight="bold"
                        />
                      </span>
                    }
                    type="text"
                    size="large"
                    style={{ background: "none" }}
                  >
                    {t("projects.components.ProjectMedias.debugMedia")}
                  </Button>
                </Space>
              </CanAccess>
            ),
          },
        ]
      : []),
  ];

  const legacyMediaPublications = mediaPublications?.map((publication) => {
    const url = `${MEDIAPLAYER_URL}?projectId=${project.id}&publicationId=${publication.project_publication_id}`;

    const open = () => {
      window.open(url);
    };

    return {
      key: publication.project_publication_id,
      label: (
        <Flex
          justify="space-between"
          align="center"
          style={{
            flex: "1 0 100%",
            padding: "0px 15px",
          }}
          key={publication.project_publication_id}
          gap={5}
        >
          <Text style={{ flex: "1" }} strong ellipsis>
            {publication.project_publication_id}
          </Text>
          <Button onClick={open} icon={<EyeOutlined />} type="text"></Button>
        </Flex>
      ),
    };
  });

  const menuProps = {
    items: menuItems,
  };

  const oldPublicationsMenuProps = {
    items: legacyMediaPublications,
  };

  return (
    <HoverableCard
      style={{
        background: isLive || ["Generated", "Ready"].includes(media.status)
          ? `linear-gradient(
                                rgba(59, 212, 202, 0.6),
                                rgba(59, 212, 202, 0.6)
                              ), url(${backgroundImg})`
          : `linear-gradient(
                                rgba(32, 135, 128, 0.4),
                                rgba(32, 135, 128, 0.4)
                              ), url(${backgroundImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: 300,
        minWidth: 300,
      }}
      variant="outlined"
      hoverable
      size="default"
      styles={{
        header: {
          border: 0,
          padding: 0,
        },

        body: {
          paddingTop: 20,
          paddingBottom: 10,
        },
        actions: {
          border: 0,
          background: "none",
          display: "flex",
          gap: 0,
          flexDirection: "column",
        },

        extra: {
          margin: "20px",
        },
      }}
      extra={
        <Flex vertical justify="space-between">
          <Flex vertical>
            {storyboard?.status === "Approved" &&
              !isLive && (
                <Button
                  onClick={() => {
                    go({
                      to: `/media/projects/${project?.id}/storyboard/${storyboard?.asset_id}`,
                    });
                  }}
                  icon={
                    <span className="anticon">
                      <BookOpenTextIcon size={24} weight="bold" />
                    </span>
                  }
                  type="text"
                  size="large"
                  style={{
                    color: "white",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                  }}
                >
                  {t("projects.components.ProjectMedias.editStoryboard")}
                </Button>
              )}
            {media.url && (
              <PreviewVideoButton url={media.url || ""} size="large">
                {t("projects.components.ProjectMedias.watchVideo")}
              </PreviewVideoButton>
            )}
            {Boolean(media.id) &&
              ["Ready", "Generated"].includes(media.status) && (
                <>
                  <Button
                    onClick={() => onEditCuePoints(media.id)}
                    icon={
                      <span className="anticon">
                        <ClockCountdownIcon size={24} weight="bold" />
                      </span>
                    }
                    type="text"
                    size="large"
                    style={{
                      color: "white",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {t("projects.components.ProjectMedias.documentAnimation")}
                  </Button>
                  <Button
                    onClick={() => onEditTemplates(media.id)}
                    icon={
                      <span className="anticon">
                        <VideoIcon size={24} weight="bold" />
                      </span>
                    }
                    type="text"
                    size="large"
                    style={{
                      color: "white",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                    }}
                  >
                    {t("projects.components.ProjectMedias.templatesEditor")}
                  </Button>
                </>
              )}
            {media.id &&
              (isLive || ["Ready", "Generated"].includes(media.status)) && (
                <Space></Space>
              )}
          </Flex>
        </Flex>
      }
    >
      {/* show old publications with multiple items */}
      {(mediaPublications?.length ?? 0) > 1 && (
        <div
          className="media-card-dropdown"
          style={{ position: "absolute", bottom: 18, right: 74 }}
        >
          <Dropdown
            menu={oldPublicationsMenuProps}
            placement="topLeft"
            trigger={["click"]}
          >
            <QueueIcon size={32} color={"#fff"} weight="bold" />
          </Dropdown>
        </div>
      )}
      {/* show first publication link */}
      {mediaPublications?.[0]?.project_publication_id && (
        <Button
          size="large"
          type="text"
          // shape="circle"
          onClick={openPublication}
          style={{ position: "absolute", bottom: 20, right: 25 }}
        >
          <Flex align="center" gap={10}>
            <Typography.Text
              type="secondary"
              strong
              style={{ fontSize: 16, color: "white" }}
            >
              {t("projects.components.ProjectMedias.openExperience")}
            </Typography.Text>
            <ArrowRightIcon
              size={24}
              weight="bold"
              style={{
                color: "white",
              }}
            />
          </Flex>
        </Button>
      )}
      <div
        className="media-card-dropdown"
        style={{ position: "absolute", bottom: 18, left: 28 }}
      >
        <Dropdown menu={menuProps} trigger={["click"]}>
          <DotsThreeVerticalIcon size={32} color={"#fff"} weight="bold" />
        </Dropdown>
      </div>

      <Modal
        title={t("projects.components.ProjectMedias.attachAMedia")}
        open={openedModal}
        onCancel={() => closeModal()}
        footer={null}
        styles={{ body: { padding: 30 } }}
      >
        <ProjectMediaUpload
          projectData={project}
          media={media}
          onUploadSuccess={() => {
            const refresh = async () => {
              refetchProjectDetail();
              await invalidate({
                resource: `media/projects/${project.id}/assets?size=50`,
                invalidates: ["list"],
              });
              // invalidate publications for the upload my video flow, recreating a new auto publication
              // not working properly because the video processing is async.
              // we should wait some BE event
              await invalidate({
                resource: `media/projects/${project.id}/publications`,
                invalidates: ["list"],
              });
            };
            refresh();
            closeModal();
          }}
        />
      </Modal>
      {media.status === "Generating" && (
        <Flex
          justify="center"
          align="center"
          style={{
            position: "absolute",
            bottom: "45%",
            width: "90%",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <ProjectProgress
            creationTime={media.created ?? null}
            processDuration={media.created ? 10 : 0}
            colors={{ from: "#fff", to: "#fff" }}
          />
        </Flex>
      )}
    </HoverableCard>
  );
};
